import { getHouses as apiGetHouses, ApiError } from './apiService';

/**
 * Viviendas registradas (`GET /v1/houses`), usadas para la vista
 * "Registrar una visita" (elegir una vivienda ya dada de alta y
 * capturar los formularios de visita sobre ella). Se cachea en
 * localStorage para poder verla sin conexión, igual que
 * `surveyService.js`/`dynamicFormService.js`.
 */

const LIST_CACHE_KEY = 'pulso17.houses.cache.list';

function isOffline(err) {
  return err instanceof ApiError && err.status === 0;
}

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage lleno o no disponible: seguimos sin caché.
  }
}

function cacheList(houses) {
  writeCache(LIST_CACHE_KEY, houses);
}

function getCachedList() {
  return readCache(LIST_CACHE_KEY) ?? [];
}

/**
 * Vivienda ya normalizada.
 * @typedef {Object} House
 * @property {string} id
 * @property {string} localUuid
 * @property {{
 *   electoralSection: string,
 *   block: string,
 *   neighborhood: string,
 *   street: string,
 *   exteriorNumber: string,
 *   reference: string,
 * }} address
 * @property {string} capturedAt
 * @property {string|null} lastVisit
 * @property {number} visitsCount
 * @property {number} completedSurveys
 * @property {number} pendingSurveys
 */
function normalizeHouse(house) {
  const address = house.address || {};
  return {
    id: String(house.id),
    localUuid: house.local_uuid,
    address: {
      electoralSection: address.electoral_section ?? '',
      block: address.block ?? '',
      neighborhood: address.neighborhood ?? '',
      street: address.street ?? '',
      exteriorNumber: address.exterior_number ?? '',
      reference: address.reference ?? '',
    },
    capturedAt: house.captured_at,
    lastVisit: house.last_visit ?? null,
    visitsCount: Number(house.visits_count ?? 0),
    completedSurveys: Number(house.completed_surveys ?? 0),
    pendingSurveys: Number(house.pending_surveys ?? 0),
  };
}

/**
 * Lista de viviendas ya normalizadas. Cae a la última copia guardada
 * localmente si no hay conexión.
 *
 * @returns {Promise<House[]>}
 */
export async function getHouses() {
  try {
    const response = await apiGetHouses();
    const rows = Array.isArray(response?.data) ? response.data : [];
    const houses = rows.map(normalizeHouse);
    cacheList(houses);
    return houses;
  } catch (err) {
    if (isOffline(err)) return getCachedList();
    throw err;
  }
}
